"use client";

// ForgetSheet — "forget this", asked twice in the same place.
//
// Pressing "forget this page" in the options menu is the first ask: it opens
// this sheet, which fetches exactly what would go (the same preview a chat
// gets from innernet_forget) and shows it — the title, what it says, anything
// that goes with it, and plainly that there is no undo. The second ask is the
// pill in the fixed bottom-right slot (law_open_dont_unfold): pressing it sends
// the preview's own code back, so what is deleted is exactly what was shown. If
// the memory changed in between, the server shows it again instead.
//
// One implementation behind it (lib/mcp/forget.ts, via POST /api/os/forget).
//
// It is the sheet, small (law_sheet_rises): the title and one meta line in its
// head, what would go in its body, the blast-radius line last in the body so it
// sits right above the foot, and the foot is the whole decision, "keep it" quiet
// on the left and the pill in the corner. The preview loads after it opens and
// a sheet is measured once, so the body holds the room the quote will need.

import { useCallback, useEffect, useState } from "react";
import EtherModal from "../ether/EtherModal";
import { useSheetLeave } from "../../lib/os/useSheetLeave";
import { PillButton } from "../ui";

export interface ForgetTarget {
  kind: "page" | "cell";
  projectId: string;
  /** the page's raw name, or the cell's `<type>-<node_id>` */
  name: string;
  /** what the page is called in the dashboard, shown while the preview loads */
  title: string;
}

interface Preview {
  confirmation_required?: boolean;
  changed_since_you_looked?: boolean;
  forgetting?: { title: string; where: string; says: string };
  also_removes?: string[];
  confirm_with?: string;
  ok?: boolean;
  error?: string;
}

export default function ForgetSheet({
  target,
  onClose,
  onForgotten,
}: {
  target: ForgetTarget;
  onClose: () => void;
  onForgotten: (title: string) => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ask = useCallback(
    async (confirm?: string) => {
      const res = await fetch("/api/os/forget", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: target.kind, project_id: target.projectId, name: target.name, confirm }),
      });
      return (await res.json()) as Preview;
    },
    [target],
  );

  // the first ask: what, exactly, would go.
  useEffect(() => {
    let live = true;
    void ask().then((p) => {
      if (!live) return;
      if (p.error) setErr(p.error);
      else setPreview(p);
    }).catch(() => live && setErr("couldn't reach your memory. try again."));
    return () => { live = false; };
  }, [ask]);

  // the second ask: the code from the preview, and nothing else, deletes.
  const forget = async () => {
    if (!preview?.confirm_with || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const out = await ask(preview.confirm_with);
      if (out.ok) {
        onForgotten(preview.forgetting?.title ?? target.title);
        return;
      }
      if (out.confirmation_required) {
        // it changed after you looked: show the new state and ask again.
        setPreview(out);
        setErr("it changed since this opened. here it is as it stands now.");
      } else {
        setErr(out.error ?? "that didn't go through. nothing was forgotten.");
      }
    } catch {
      setErr("that didn't go through. nothing was forgotten.");
    } finally {
      setBusy(false);
    }
  };

  const title = preview?.forgetting?.title ?? target.title;
  const also = preview?.also_removes ?? [];
  // mounted by its caller only while it is wanted: it sinks, then the caller hears
  const { open, leave } = useSheetLeave(onClose);

  return (
    <EtherModal
      open={open}
      onClose={leave}
      labelledBy="fg-title"
      size="sm"
      height="fit"
      header={
        <>
          <h2 id="fg-title" className="eth-title">forget {title}?</h2>
          <p className="eth-meta">this is permanent. there is no undo.</p>
        </>
      }
      footStart={
        <button type="button" className="quiet-link" onClick={leave} disabled={busy}>
          keep it
        </button>
      }
      footer={
        <PillButton onClick={() => void forget()} disabled={busy || !preview?.confirm_with}>
          {busy ? "forgetting…" : "forget it"}
        </PillButton>
      }
    >
      <div className="fg-body">
        {preview?.forgetting?.where ? <p className="fg-where">{preview.forgetting.where}</p> : null}
        {preview?.forgetting?.says ? (
          <blockquote className="fg-says">{preview.forgetting.says}</blockquote>
        ) : !err ? (
          <p className="fg-wait">finding exactly what would go…</p>
        ) : null}
        {also.length > 0 && (
          <p className="fg-also">it also removes {also.join(", and ")}.</p>
        )}
      </div>
      <p className="rt-says" data-warn={err ? "" : undefined}>
        {err ?? (preview?.confirm_with ? "only this one memory goes. everything else stays." : "")}
      </p>
    </EtherModal>
  );
}
